import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { genStyles } from '../converter-layout/generator-layout';
import styles from './number-base-converter.component.module.scss';

interface BaseState {
    binary: string;
    octal: string;
    decimal: string;
    hex: string;
    error: string;
    activeField: string | null;
    copiedField: string | null;
}

const MAX_SAFE = Number.MAX_SAFE_INTEGER; // 2^53 - 1

function formatBinary(bin: string): string {
    const clean = bin.replace(/\s/g, '');
    if (!clean) return '';
    const padded = clean.padStart(Math.ceil(clean.length / 4) * 4, '0');
    return padded.match(/.{1,4}/g)?.join(' ') || clean;
}

function isValidForBase(input: string, base: number): boolean {
    const clean = input.replace(/\s/g, '');
    if (!clean) return true;
    const chars = base <= 10
        ? `0-${base - 1}`
        : `0-9a-${String.fromCharCode(86 + base)}`;
    return new RegExp(`^[${chars}]+$`, 'i').test(clean);
}

export default class NumberBaseConverterComponent extends Component<{}, BaseState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    state: BaseState = {
        binary: '1111 1111', octal: '377', decimal: '255', hex: 'FF',
        error: '', activeField: null, copiedField: null,
    };

    convert(value: string, fromBase: number, fieldName: string) {
        const clean = value.replace(/\s/g, '');

        if (!clean) {
            this.setState({ binary: '', octal: '', decimal: '', hex: '', error: '', activeField: fieldName });
            return;
        }

        if (!isValidForBase(clean, fromBase)) return; // silently reject invalid chars

        const num = parseInt(clean, fromBase);
        if (isNaN(num)) return;

        if (num > MAX_SAFE) {
            this.setState({ error: 'Number exceeds safe integer range (2⁵³ − 1)', activeField: fieldName, [fieldName]: value } as any);
            return;
        }

        this.setState({
            binary: formatBinary(num.toString(2)),
            octal: num.toString(8),
            decimal: num.toString(10),
            hex: num.toString(16).toUpperCase(),
            error: '',
            activeField: fieldName,
            [fieldName]: value, // preserve user's raw input for the active field
        } as any);
    }

    handleInput = (fieldName: string, base: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        // For binary, allow spaces for grouping
        if (fieldName === 'binary') {
            const cleaned = raw.replace(/[^01\s]/g, '');
            if (cleaned !== raw) return; // reject invalid chars silently
        }
        this.convert(raw, base, fieldName);
    };

    copyToClipboard = (value: string, field: string) => {
        const clean = value.replace(/\s/g, '');
        navigator.clipboard.writeText(clean).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: field });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    copyAll = () => {
        const { binary, octal, decimal, hex } = this.state;
        const text = `Binary: ${binary.replace(/\s/g, '')} | Octal: ${octal} | Decimal: ${decimal} | Hex: ${hex}`;
        navigator.clipboard.writeText(text).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: 'all' });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    clear = () => {
        this.setState({ binary: '', octal: '', decimal: '', hex: '', error: '', activeField: null, copiedField: null });
    };

    renderField(label: string, sublabel: string, fieldName: keyof BaseState, base: number, placeholder: string) {
        const value = this.state[fieldName] as string;
        const isCopied = this.state.copiedField === fieldName;
        const isActive = this.state.activeField === fieldName;

        return (
            <div className={styles.fieldGroup}>
                <div className={styles.fieldHeader}>
                    <label className={sharedStyles.label} htmlFor={`base-${fieldName}`}>
                        {label} <span className={styles.sublabel}>({sublabel})</span>
                    </label>
                    <button
                        className={`${sharedStyles.copyButton} ${isCopied ? sharedStyles.copyButtonSuccess : ''}`}
                        onClick={() => this.copyToClipboard(value, fieldName)}
                        aria-label={`Copy ${label} value`}
                    >
                        {isCopied ? '✓ Copied' : 'Copy'}
                    </button>
                </div>
                <input
                    id={`base-${fieldName}`}
                    type="text"
                    className={`${styles.baseInput} ${isActive ? styles.activeInput : ''}`}
                    value={value}
                    onChange={this.handleInput(fieldName, base)}
                    placeholder={placeholder}
                    autoComplete="off"
                    spellCheck={false}
                />
            </div>
        );
    }

    render() {
        const { error, copiedField } = this.state;
        const isCopiedAll = copiedField === 'all';

        return (
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={styles.fields}>
                            {this.renderField('Binary', 'Base 2', 'binary', 2, '1111 1111')}
                            {this.renderField('Octal', 'Base 8', 'octal', 8, '377')}
                            {this.renderField('Decimal', 'Base 10', 'decimal', 10, '255')}
                            {this.renderField('Hexadecimal', 'Base 16', 'hex', 16, 'FF')}
                        </div>
                        {error && <div className={sharedStyles.errorMessage} aria-live="polite">⚠ {error}</div>}
                    </div>
                </div>
                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={styles.actions}>
                            <button
                                className={`${sharedStyles.copyButton} ${isCopiedAll ? sharedStyles.copyButtonSuccess : ''}`}
                                onClick={this.copyAll}
                            >
                                {isCopiedAll ? '✓ Copied All' : 'Copy All'}
                            </button>
                            <button className={sharedStyles.copyButton} onClick={this.clear}>✕ Clear</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
